import React, { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, X, Search, Upload, GripVertical, Eye, EyeOff, ShieldCheck } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";

const RED = "#D62B3A";
const CATEGORIES = ["Führung", "Zentrale Funktionen", "Standort/Operations", "Sonstige"];
const DEPARTMENTS = ["Führung", "Zentrale", "Standort"];

async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
}

const inp = "w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#D62B3A] outline-none text-sm";
const Label = ({ children }) => <label className="block text-xs font-semibold text-gray-600 mb-1">{children}</label>;

// ==================== ROLES ====================
export const TeamRoles = () => {
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => api.get("/admin/team/roles").then((r) => setRoles(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const body = { role_name: form.role_name, role_category: form.role_category, display_priority: Number(form.display_priority) };
      if (form.id) await api.put(`/admin/team/roles/${form.id}`, body);
      else await api.post("/admin/team/roles", body);
      toast.success("Rolle gespeichert"); setForm(null); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Rolle löschen? Sie wird von allen Personen entfernt.")) return; await api.delete(`/admin/team/roles/${id}`); toast.success("Gelöscht"); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">Rollen frei verwaltbar – z. B. künftig „Bauleiter" (Construction) oder „Projektmanager Energie".</p>
        <button onClick={() => setForm({ role_name: "", role_category: "Zentrale Funktionen", display_priority: 50 })} data-testid="role-add-btn" className="inline-flex items-center gap-2 bg-[#D62B3A] text-white text-sm font-semibold px-4 py-2 rounded-lg"><Plus className="w-4 h-4" /> Rolle</button>
      </div>
      <div className="bg-white rounded-xl border border-gray-100 divide-y">
        {roles.map((r) => (
          <div key={r.id} className="flex items-center gap-3 px-4 py-3 text-sm" data-testid={`role-row-${r.id}`}>
            <span className="w-10 text-gray-400">#{r.display_priority}</span>
            <span className="font-semibold flex-1">{r.role_name}</span>
            <span className="text-gray-500">{r.role_category}</span>
            <button onClick={() => setForm(r)} className="p-1.5 rounded hover:bg-gray-100"><Pencil className="w-4 h-4" /></button>
            <button onClick={() => del(r.id)} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {roles.length === 0 && <div className="px-4 py-6 text-center text-gray-400 text-sm">Noch keine Rollen.</div>}
      </div>

      {form && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setForm(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4"><h3 className="font-bold">{form.id ? "Rolle bearbeiten" : "Neue Rolle"}</h3><button onClick={() => setForm(null)}><X className="w-5 h-5" /></button></div>
            <div className="space-y-3">
              <div><Label>Rollenname</Label><input className={inp} value={form.role_name} onChange={(e) => setForm({ ...form, role_name: e.target.value })} data-testid="role-name-input" /></div>
              <div><Label>Kategorie</Label><select className={inp} value={form.role_category} onChange={(e) => setForm({ ...form, role_category: e.target.value })} data-testid="role-category-select">{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></div>
              <div><Label>Priorität (kleiner = weiter oben)</Label><input type="number" className={inp} value={form.display_priority} onChange={(e) => setForm({ ...form, display_priority: e.target.value })} data-testid="role-priority-input" /></div>
            </div>
            <button onClick={save} data-testid="role-save-btn" className="mt-5 w-full bg-[#D62B3A] text-white font-semibold py-2.5 rounded-lg">Speichern</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== ROLE MULTI-SELECT ====================
const RolePicker = ({ roles, value, onChange }) => {
  const [q, setQ] = useState("");
  const selected = roles.filter((r) => value.includes(r.id));
  const results = roles.filter((r) => r.role_name.toLowerCase().includes(q.toLowerCase()) && !value.includes(r.id));
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {selected.map((r) => (
          <span key={r.id} className="inline-flex items-center gap-1 bg-[#FDF2F4] text-[#D62B3A] text-xs font-semibold px-2.5 py-1 rounded-full" data-testid={`role-tag-${r.id}`}>
            {r.role_name}<button onClick={() => onChange(value.filter((v) => v !== r.id))}><X className="w-3 h-3" /></button>
          </span>
        ))}
        {selected.length === 0 && <span className="text-xs text-gray-400">Keine Rolle zugewiesen</span>}
      </div>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
        <input className={inp + " pl-8"} placeholder="Rolle suchen & hinzufügen…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="member-role-search" />
      </div>
      {q && (
        <div className="mt-1 border border-gray-100 rounded-lg divide-y max-h-40 overflow-y-auto">
          {results.map((r) => (
            <button key={r.id} onClick={() => { onChange([...value, r.id]); setQ(""); }} data-testid={`role-option-${r.id}`} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50">{r.role_name} <span className="text-gray-400">· {r.role_category}</span></button>
          ))}
          {results.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">Keine Treffer</div>}
        </div>
      )}
    </div>
  );
};

// ==================== MEMBERS ====================
export const TeamMembers = () => {
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/admin/team/members").then((r) => setMembers(r.data));
  useEffect(() => {
    load();
    api.get("/admin/team/roles").then((r) => setRoles(r.data));
    api.get("/branches").then((r) => setBranches(r.data));
  }, []);

  const empty = { full_name: "", role_ids: [], branch_id: "", department: "Zentrale", short_bio: "", photo_url: null, display_order: 0, is_visible: true, consent_given: false, linkedin_url: "", start_date: "" };

  const save = async () => {
    if (!form.full_name.trim()) { toast.error("Name erforderlich"); return; }
    if (form.is_visible && !form.consent_given) { toast.error("Ohne Einwilligung (Consent) darf die Person nicht sichtbar sein."); return; }
    try {
      const body = { ...form, branch_id: form.branch_id || null, display_order: Number(form.display_order || 0) };
      if (form.id) await api.put(`/admin/team/members/${form.id}`, body);
      else await api.post("/admin/team/members", body);
      toast.success("Gespeichert"); setForm(null); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Person endgültig löschen? (DSGVO)")) return; await api.delete(`/admin/team/members/${id}`); toast.success("Gelöscht"); load(); };
  const onFile = async (e) => { const f = e.target.files?.[0]; if (!f) return; setUploading(true); try { const url = await uploadImage(f); setForm((p) => ({ ...p, photo_url: url })); } catch { toast.error("Upload fehlgeschlagen"); } setUploading(false); };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">Führung, zentrale Funktionen & Standort-Fachpersonal. Foto & Name erscheinen NUR mit Einwilligung.</p>
        <button onClick={() => setForm(empty)} data-testid="member-add-btn" className="inline-flex items-center gap-2 bg-[#D62B3A] text-white text-sm font-semibold px-4 py-2 rounded-lg"><Plus className="w-4 h-4" /> Person</button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {members.map((m) => (
          <div key={m.id} className="bg-white rounded-xl border border-gray-100 p-4 flex gap-3" data-testid={`member-row-${m.id}`}>
            {m.photo_url ? <img src={m.photo_url} alt="" className="w-12 h-12 rounded-full object-cover" /> : <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: RED }}>{(m.full_name || "H").slice(0, 1)}</div>}
            <div className="flex-1 min-w-0">
              <div className="font-semibold flex items-center gap-2">{m.full_name}
                {m.is_visible ? <Eye className="w-3.5 h-3.5 text-emerald-500" /> : <EyeOff className="w-3.5 h-3.5 text-gray-400" />}
                {m.consent_given && <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />}
              </div>
              <div className="text-xs text-[#D62B3A] font-medium truncate">{(m.role_titles || []).join(" · ") || "—"}</div>
            </div>
            <div className="flex flex-col gap-1">
              <button onClick={() => setForm({ ...m, branch_id: m.branch_id || "", short_bio: m.short_bio || "", linkedin_url: m.linkedin_url || "", start_date: m.start_date || "" })} className="p-1.5 rounded hover:bg-gray-100"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => del(m.id)} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {members.length === 0 && <div className="col-span-2 px-4 py-8 text-center text-gray-400 text-sm">Noch keine Personen. Servicepersonal ohne Rolle wird ausschließlich über das Standort-Gruppenfoto gezeigt.</div>}
      </div>

      {form && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setForm(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4"><h3 className="font-bold">{form.id ? "Person bearbeiten" : "Neue Person"}</h3><button onClick={() => setForm(null)}><X className="w-5 h-5" /></button></div>
            <div className="space-y-3">
              <div><Label>Voller Name</Label><input className={inp} value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="member-name-input" /></div>
              <div><Label>Rollen (Mehrfachauswahl)</Label><RolePicker roles={roles} value={form.role_ids} onChange={(v) => setForm({ ...form, role_ids: v })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Bereich</Label><select className={inp} value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="member-dept-select">{DEPARTMENTS.map((d) => <option key={d}>{d}</option>)}</select></div>
                <div><Label>Standort (optional)</Label><select className={inp} value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })} data-testid="member-branch-select"><option value="">— zentral / kein Standort —</option>{branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</select></div>
              </div>
              <div><Label>Kurzbio (max. 400 Zeichen, optional)</Label><textarea maxLength={400} rows={3} className={inp} value={form.short_bio} onChange={(e) => setForm({ ...form, short_bio: e.target.value })} data-testid="member-bio-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Sortierung</Label><input type="number" className={inp} value={form.display_order} onChange={(e) => setForm({ ...form, display_order: e.target.value })} /></div>
                <div><Label>Im Team seit (optional)</Label><input type="date" className={inp} value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
              </div>
              <div><Label>LinkedIn URL (optional)</Label><input className={inp} value={form.linkedin_url} onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })} /></div>
              <div>
                <Label>Profilfoto (optional · Empfehlung 1:1, min. 400×400)</Label>
                <div className="flex items-center gap-3">
                  {form.photo_url ? <img src={form.photo_url} alt="" className="w-14 h-14 rounded-full object-cover" /> : <div className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: RED }}>{(form.full_name || "H").slice(0, 1)}</div>}
                  <label className="inline-flex items-center gap-2 text-sm border border-gray-200 rounded-lg px-3 py-2 cursor-pointer hover:bg-gray-50"><Upload className="w-4 h-4" /> {uploading ? "Lädt…" : "Foto hochladen"}<input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="member-photo-input" /></label>
                  {form.photo_url && <button onClick={() => setForm({ ...form, photo_url: null })} className="text-xs text-gray-400 hover:text-red-500">Entfernen</button>}
                </div>
                <p className="text-xs text-gray-400 mt-1">Ohne Foto wird automatisch ein Initialen-Avatar angezeigt.</p>
              </div>
              <label className="flex items-start gap-2 bg-[#FDF2F4] rounded-lg p-3 cursor-pointer">
                <input type="checkbox" checked={form.consent_given} onChange={(e) => setForm({ ...form, consent_given: e.target.checked })} className="mt-0.5 accent-[#D62B3A]" data-testid="member-consent-checkbox" />
                <span className="text-xs text-gray-700"><strong>Einwilligung liegt vor.</strong> Foto & Name werden ohne Einwilligung niemals öffentlich angezeigt (DSGVO).</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_visible} onChange={(e) => setForm({ ...form, is_visible: e.target.checked })} className="accent-[#D62B3A]" data-testid="member-visible-checkbox" />
                <span className="text-sm text-gray-700">Öffentlich sichtbar</span>
              </label>
            </div>
            <button onClick={save} data-testid="member-save-btn" className="mt-5 w-full bg-[#D62B3A] text-white font-semibold py-2.5 rounded-lg">Speichern</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== BRANCH TEAMS ====================
export const BranchTeams = () => {
  const [teams, setTeams] = useState([]);
  const [uploadingId, setUploadingId] = useState(null);
  const load = () => api.get("/admin/team/branch-teams").then((r) => setTeams(r.data));
  useEffect(() => { load(); }, []);

  const setVal = (bid, patch) => setTeams((ts) => ts.map((t) => (t.branch_id === bid ? { ...t, ...patch } : t)));
  const save = async (t) => {
    try {
      await api.put(`/admin/team/branch-teams/${t.branch_id}`, { group_photo_url: t.group_photo_url || null, team_description: t.team_description || "", team_size_display: t.team_size_display || "", is_visible: t.is_visible });
      toast.success("Standort-Team gespeichert");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const onFile = async (bid, e) => { const f = e.target.files?.[0]; if (!f) return; setUploadingId(bid); try { const url = await uploadImage(f); setVal(bid, { group_photo_url: url }); } catch { toast.error("Upload fehlgeschlagen"); } setUploadingId(null); };

  return (
    <div>
      <p className="text-sm text-gray-500 mb-4">Anonymes Gruppenfoto pro Standort (Empfehlung 16:9). Servicepersonal wird ausschließlich hier – ohne Namen – gezeigt.</p>
      <div className="space-y-4">
        {teams.map((t) => (
          <div key={t.branch_id} className="bg-white rounded-xl border border-gray-100 p-4" data-testid={`branchteam-row-${t.branch_id}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold">{t.branch_name}</div>
              <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={t.is_visible} onChange={(e) => setVal(t.branch_id, { is_visible: e.target.checked })} className="accent-[#D62B3A]" data-testid={`branchteam-visible-${t.branch_id}`} /> Sichtbar</label>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                {t.group_photo_url ? <img src={t.group_photo_url} alt="" className="w-full h-40 object-cover rounded-lg" /> : <div className="w-full h-40 rounded-lg flex items-center justify-center text-white text-sm" style={{ backgroundColor: "#6F6F6F" }}>Kein Foto – Platzhalter aktiv</div>}
                <label className="mt-2 inline-flex items-center gap-2 text-sm border border-gray-200 rounded-lg px-3 py-2 cursor-pointer hover:bg-gray-50"><Upload className="w-4 h-4" /> {uploadingId === t.branch_id ? "Lädt…" : "Gruppenfoto"}<input type="file" accept="image/*" className="hidden" onChange={(e) => onFile(t.branch_id, e)} data-testid={`branchteam-photo-${t.branch_id}`} /></label>
              </div>
              <div className="space-y-2">
                <div><Label>Beschreibung (max. 300)</Label><textarea maxLength={300} rows={3} className={inp} value={t.team_description || ""} onChange={(e) => setVal(t.branch_id, { team_description: e.target.value })} data-testid={`branchteam-desc-${t.branch_id}`} /></div>
                <div><Label>Team-Größe (optional, z. B. „12 engagierte Mitarbeitende")</Label><input className={inp} value={t.team_size_display || ""} onChange={(e) => setVal(t.branch_id, { team_size_display: e.target.value })} /></div>
                <button onClick={() => save(t)} data-testid={`branchteam-save-${t.branch_id}`} className="w-full bg-[#D62B3A] text-white font-semibold py-2 rounded-lg text-sm">Speichern</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
